import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  tap,
} from 'rxjs/operators';

import { AccountService } from '../../core/auth/account.service';
import { AlertService } from '../../shared/alert/alert.service';
import { LocalStorageService } from 'ngx-webstorage';
import { LoginService } from '../../core/login/login.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

interface Location {
  id: string;
  location_name: string;
}

@Component({
  selector: 'app-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [AlertService],
})
export class LoginComponent implements OnInit {
  @ViewChild('password') passwordInput: ElementRef | undefined;

  authenticationError = false;
  hide = true;

  locations: Location[] = [];
  selectedLocationId: string | null = null;
  loginForm!: FormGroup;
  private usernameSub?: Subscription;
  loadingLocations = false;

  get hasLocations(): boolean {
    return this.locations.length > 0;
  }

  get safeLocations(): Location[] {
    return this.locations || [];
  }

  constructor(
    private alertService: AlertService,
    private loginService: LoginService,
    private router: Router,
    private fb: FormBuilder,
    private accountService: AccountService,
    public localStorageService: LocalStorageService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required]],
      location: ['', [Validators.required]],
    });
    this.translateService.use('sr');

    // Load default location on init
    this.loadDefaultLocation();

    // Debounced username watcher to handle typing/autocomplete without UI stutter
    this.usernameSub = this.loginForm
      .get('username')!
      .valueChanges.pipe(
        filter((v: string) => !!v && v.trim().length > 2),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.loadingLocations = true)),
        switchMap((username: string) =>
          this.loginService
            .getLocationsForEmail(username)
            .pipe(finalize(() => (this.loadingLocations = false)))
        )
      )
      .subscribe({
        next: (res) => {
          if (res && res.data && res.data.length) {
            this.locations = res.data;
            this.loginForm.controls['location'].setValue(this.locations[0].id);
          } else {
            this.loadDefaultLocation();
          }
        },
        error: () => {
          this.loadDefaultLocation();
        },
      });
  }

  login(): void {
    const hasRealLocation =
      this.locations && this.locations.some((loc: Location) => loc.id !== 'default');

    if (!hasRealLocation) {
      this.alertService.error(this.translateService.instant('login.messages.error.noLocation'));
      return;
    }
    const selectedLocation = this.loginForm.get('location')?.value;
    if (selectedLocation) {
      this.localStorageService.store('location', selectedLocation);
      const selectedLocationName =
        this.locations.find((loc: Location) => loc.id === selectedLocation)?.location_name ?? null;
      if (selectedLocationName) {
        this.localStorageService.store('locationName', selectedLocationName);
      }
    }
    const username = this.loginForm.get('username')!.value;
    this.loginService
      .login({
        username: username,
        password: this.loginForm.get('password')!.value,
        rememberMe: true,
      })
      .subscribe({
        next: (res) => {
          this.authenticationError = false;
          this.alertService.success(this.translateService.instant('login.messages.success'));
          if (res) {
            const stored = this.localStorageService.retrieve('locale');
            const rawLang = (res as any).langKey;
            // sanitize and fallback: prefer backend value, else stored, else 'sr'
            let lang =
              (typeof rawLang === 'string'
                ? rawLang.replace(/\"/g, '').replace(/"/g, '')
                : rawLang) ||
              stored ||
              'sr';
            if (lang !== 'sr' && lang !== 'en') {
              lang = 'sr';
            }
            this.localStorageService.store('locale', lang);
            this.accountService.setSessionLanguage(lang).subscribe();
            this.translateService.use(lang);
          }
          // Ensure location is set after successful login
          const existingLocation = this.localStorageService.retrieve('location');
          if (!existingLocation && username) {
            this.loginService.getLocationsForEmail(username).subscribe({
              next: (locationsRes) => {
                if (locationsRes && locationsRes.data && locationsRes.data.length) {
                  const locationId = locationsRes.data[0].id;
                  const locationName = locationsRes.data[0].location_name;
                  console.log(
                    '[LoginComponent.login] storing location to localStorage after login:',
                    locationId
                  );
                  this.localStorageService.store('location', locationId);
                  if (locationName) {
                    this.localStorageService.store('locationName', locationName);
                  }
                }
                this.router.navigate(['home']);
              },
              error: () => {
                this.router.navigate(['home']);
              },
            });
          } else {
            this.router.navigate(['home']);
          }
        },
        error: () => {
          this.authenticationError = true;
          this.alertService.error(
            this.translateService.instant('login.messages.error.authentication')
          );
        },
      });
  }

  register(): void {
    this.router.navigate(['/account/register']);
  }

  requestResetPassword(): void {
    this.router.navigate(['/account/reset', 'request']);
  }

  changeLanguage(languageKey: string): void {
    this.localStorageService.store('locale', languageKey);
    this.translateService.use(languageKey);
    this.accountService.setSessionLanguage(languageKey).subscribe();

    // Update the default location text when language changes
    this.updateDefaultLocationText();
  }

  updateDefaultLocationText(): void {
    if (this.locations && this.locations.length > 0) {
      const defaultLocation = this.locations.find((loc: Location) => loc.id === 'default');
      if (defaultLocation) {
        const defaultLocationText = this.translateService.instant('login.defaultLocation');
        const locationName =
          defaultLocationText !== 'login.defaultLocation'
            ? defaultLocationText
            : this.translateService.currentLang === 'sr'
              ? 'Glavna lokacija'
              : 'Main location';
        defaultLocation.location_name = locationName;
      }
    }
  }

  loadDefaultLocation(): void {
    // Load a default location to always show the dropdown
    // This ensures the location field is always visible
    const defaultLocationText = this.translateService.instant('login.defaultLocation');

    // If translation is not ready, use fallback text
    const locationName =
      defaultLocationText !== 'login.defaultLocation'
        ? defaultLocationText
        : this.translateService.currentLang === 'sr'
          ? 'Glavna lokacija'
          : 'Main location';

    this.locations = [
      {
        id: 'default',
        location_name: locationName,
      },
    ];
    this.loginForm.controls['location'].setValue('default');
  }

  getLocationsForEmail(): void {
    const username = this.loginForm.get('username')!.value;
    if (!username) return;
    this.loginService.getLocationsForEmail(username).subscribe({
      next: (res) => {
        if (res && res.data && res.data.length) {
          this.locations = res.data;
          // preselect first location; user can change it
          this.loginForm.controls['location'].setValue(this.locations[0].id);
        } else {
          // Keep default location if no locations found
          this.loadDefaultLocation();
        }
      },
      error: () => {
        // Keep default location on error
        this.loadDefaultLocation();
      },
    });
  }
}
